<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import SkrDivider from '@/components/SkrDivider.vue'
import SkrAutocomplete from '@/components/SkrAutocomplete.vue'
import SuspendedBadge from '@/components/business/members/SuspendedBadge.vue'

export interface AssignableMember {
  email: string
  username: string
  status?: 'suspended'
}

const props = withDefaults(
  defineProps<{
    assignableMembers?: AssignableMember[]
    title?: string
    subtitle?: string
    cancelText?: string
  }>(),
  {
    assignableMembers: () => [],
  }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'action', data: Dialogs['business/DialogAddMember']['contextData']): void
}>()

/**
 * The selected user IDs.
 */
const selection = ref<string[]>([])
const showDialog = ref<boolean>(true)
const { t } = useI18n()

const disableSuspendedMembers = (member: AssignableMember) => ({ disabled: member.status === 'suspended' })

const addMembers = () => {
  const members = selection.value.reduce((acc: AssignableMember[], user) => {
    const member = props.assignableMembers.find(member => member.username === user)
    if (member) {
      acc.push(member)
    }
    return acc
  }, [])
  emit('action', members)
}
</script>

<template>
  <v-dialog v-model="showDialog" min-width="500" max-width="min-content" @click:outside="emit('close')">
    <v-card>
      <v-card-title data-cy="add_member_dialog">
        {{ title }}
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{ subtitle }}
        </p>
        <skr-autocomplete
          v-model="selection"
          :items="assignableMembers"
          item-title="email"
          item-value="username"
          :item-props="disableSuspendedMembers"
          :label="t('global.email')"
          :no-data-text="t('business.seals.details.members_table.add_dialog.no_data')"
          multiple
          variant="filled"
          chips
          data-cy="add_member_input"
        >
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #prepend="{ isSelected }">
                <v-checkbox-btn
                  :model-value="isSelected"
                  :disabled="itemProps.disabled"
                  :ripple="false"
                  tabindex="-1"
                />
              </template>
              <template #title>
                <div class="member-option">
                  <span>{{ item.title }}</span>
                  <suspended-badge v-if="item.raw.status === 'suspended'" />
                </div>
              </template>
            </v-list-item>
          </template>
        </skr-autocomplete>
      </v-card-text>
      <skr-divider />
      <v-card-actions class="d-flex justify-space-between">
        <skr-button size="xl" color="info" type="secondary" @click="emit('close')">
          {{ cancelText || t('global.cancel') }}
        </skr-button>
        <skr-button
          size="xl"
          color="info"
          :disabled="selection.length === 0"
          data-cy="add_member_button"
          @click="addMembers"
        >
          {{ t('global.add') }}
        </skr-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.member-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
